/**
 * Drives the admin onboarding tour (built on driver.js).
 *
 * Responsibilities:
 *  - Pick the right step set (desktop vs mobile) once, at start time.
 *  - Filter out steps whose target is not on screen (so the tour never
 *    stalls on missing demo data — dispatchers with an empty list still
 *    get a clean flow).
 *  - Persist "already seen" in localStorage per-user so the auto-start
 *    only fires the very first time a given user logs in.
 *  - Expose `start()` / `reset()` so a help button can re-trigger it.
 */

import { useCallback, useEffect, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { desktopSteps, mobileSteps } from "./tourSteps";

const STORAGE_PREFIX = "sdrive_onboarding_seen_v1:";

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || "anonymous"}`;
}

function pickSteps() {
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches;
  return isMobile ? mobileSteps : desktopSteps;
}

/**
 * Keep steps whose `element` selector actually resolves to a visible node.
 * Steps without `element` (pure modals) are always kept.
 */
function resolvableSteps(steps) {
  if (typeof document === "undefined") return steps;
  return steps.filter((s) => {
    if (!s.element) return true;
    try {
      return !!document.querySelector(s.element);
    } catch {
      return false;
    }
  });
}

export function useAdminOnboarding(user) {
  const driverRef = useRef(null);

  const start = useCallback(() => {
    // Destroy any lingering instance (user restarts while a tour is open).
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* no-op */ }
      driverRef.current = null;
    }

    const steps = resolvableSteps(pickSteps());
    if (!steps.length) return;

    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 4,
      stageRadius: 8,
      smoothScroll: true,
      popoverClass: "sdrive-onboarding-popover",
      progressText: "{{current}} / {{total}}",
      nextBtnText: "Suivant →",
      prevBtnText: "← Précédent",
      doneBtnText: "Terminer",
      onDestroyed: () => {
        if (user?.id) {
          try { localStorage.setItem(storageKey(user.id), "1"); } catch { /* ignore */ }
        }
      },
      steps,
    });
    driverRef.current = d;
    d.drive();
  }, [user?.id]);

  const reset = useCallback(() => {
    if (!user?.id) return;
    try { localStorage.removeItem(storageKey(user.id)); } catch { /* ignore */ }
  }, [user?.id]);

  // Auto-start on first login for admin-class roles. Delay a tick so the
  // dashboard DOM (ride rows, tabs, bell) is mounted before we query it.
  useEffect(() => {
    if (!user?.id) return;
    const adminLike = ["admin", "company_admin", "super_admin"].includes(user.role);
    if (!adminLike) return;
    let seen = "0";
    try { seen = localStorage.getItem(storageKey(user.id)) || "0"; } catch { /* ignore */ }
    if (seen === "1") return;
    const t = setTimeout(() => start(), 650);
    return () => clearTimeout(t);
  }, [user?.id, user?.role, start]);

  // Safety: make sure an open tour is torn down when the component unmounts.
  useEffect(() => () => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* no-op */ }
      driverRef.current = null;
    }
  }, []);

  return { start, reset };
}
